import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import GroupIcon from '@mui/icons-material/Group';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useEventAnalytics } from '../hooks/useAnalytics';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(hours: number): string {
  if (hours === 0) return '--';
  if (hours < 1) {
    const mins = Math.round(hours * 60);
    return `${mins} min${mins !== 1 ? 's' : ''}`;
  }
  if (hours < 24) {
    return `${hours.toFixed(1)} hours`;
  }
  const days = hours / 24;
  return `${days.toFixed(1)} days`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(d);
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  loading?: boolean;
}

function StatCard({ icon, label, value, subtitle, loading }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 13 } }}>
            {label}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {value}
          </Typography>
        )}
        {subtitle && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: 11, sm: 12 } }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── RSVP Breakdown Bar ─────────────────────────────────────────────────────

interface RsvpBreakdownProps {
  yes: number;
  no: number;
  maybe: number;
  pending: number;
}

const RSVP_COLORS = {
  yes: '#4caf50',
  no: '#f44336',
  maybe: '#ff9800',
  pending: '#bdbdbd',
};

function RsvpBreakdown({ yes, no, maybe, pending }: RsvpBreakdownProps) {
  const total = yes + no + maybe + pending;
  if (total === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No guests added yet.
      </Typography>
    );
  }

  const segments = [
    { label: 'Yes', count: yes, color: RSVP_COLORS.yes },
    { label: 'No', count: no, color: RSVP_COLORS.no },
    { label: 'Maybe', count: maybe, color: RSVP_COLORS.maybe },
    { label: 'Pending', count: pending, color: RSVP_COLORS.pending },
  ];

  return (
    <Box>
      {/* Stacked bar */}
      <Box sx={{ display: 'flex', height: 32, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
        {segments.map((seg) =>
          seg.count > 0 ? (
            <Box
              key={seg.label}
              sx={{
                width: `${(seg.count / total) * 100}%`,
                bgcolor: seg.color,
                minWidth: seg.count > 0 ? 4 : 0,
                transition: 'width 0.3s ease',
              }}
            />
          ) : null,
        )}
      </Box>
      {/* Labels */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {segments.map((seg) => (
          <Stack key={seg.label} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: seg.color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 13 } }}>
              {seg.label}: <strong>{seg.count}</strong>
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ─── Delivery Funnel ─────────────────────────────────────────────────────────

interface FunnelStepProps {
  label: string;
  count: number;
  pct?: number;
  isLast?: boolean;
}

function FunnelStep({ label, count, pct, isLast }: FunnelStepProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ textAlign: 'center', minWidth: 70 }}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
          {count}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 12 } }}>
          {label}
        </Typography>
      </Box>
      {!isLast && (
        <Box sx={{ mx: { xs: 0.5, sm: 1.5 }, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 11 } }}>
            {pct !== undefined ? `${pct}%` : ''}
          </Typography>
          <Typography color="text.disabled" sx={{ fontSize: { xs: 16, sm: 20 }, lineHeight: 1 }}>
            &rarr;
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function EventAnalyticsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useEventAnalytics(eventId ?? '');

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2, minHeight: 44 }}>
          Back
        </Button>
        <Alert severity="error">
          Failed to load analytics. Please try again later.
        </Alert>
      </Box>
    );
  }

  const email = data?.emailMetrics;
  const rsvp = data?.rsvpMetrics;
  const ttr = data?.timeToRsvp;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ minHeight: 44 }}>
          Back
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
          Event Analytics
        </Typography>
      </Stack>

      {/* Summary Cards — 2x2 grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<GroupIcon fontSize="small" />}
            label="Response Rate"
            value={isLoading ? '' : `${rsvp?.responseRate ?? 0}%`}
            subtitle={rsvp ? `${rsvp.responded} of ${rsvp.totalGuests}` : undefined}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<MailOutlineIcon fontSize="small" />}
            label="Open Rate"
            value={isLoading ? '' : `${email?.openRate ?? 0}%`}
            subtitle={email ? `${email.opened} of ${email.delivered}` : undefined}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<TouchAppIcon fontSize="small" />}
            label="Click Rate"
            value={isLoading ? '' : `${email?.clickRate ?? 0}%`}
            subtitle={email ? `${email.clicked} of ${email.delivered}` : undefined}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<AccessTimeIcon fontSize="small" />}
            label="Avg Time to RSVP"
            value={isLoading ? '' : formatDuration(ttr?.averageHours ?? 0)}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* RSVP Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            RSVP Breakdown
          </Typography>
          {isLoading ? (
            <Skeleton variant="rectangular" height={60} />
          ) : (
            <RsvpBreakdown
              yes={rsvp?.yes ?? 0}
              no={rsvp?.no ?? 0}
              maybe={rsvp?.maybe ?? 0}
              pending={rsvp?.pending ?? 0}
            />
          )}
        </CardContent>
      </Card>

      {/* Email Performance — Delivery Funnel */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Email Performance
          </Typography>
          {isLoading ? (
            <Skeleton variant="rectangular" height={60} />
          ) : email ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
                <FunnelStep label="Sent" count={email.totalSent} pct={email.totalSent > 0 ? Math.round((email.delivered / email.totalSent) * 100) : 0} />
                <FunnelStep label="Delivered" count={email.delivered} pct={email.delivered > 0 ? Math.round((email.opened / email.delivered) * 100) : 0} />
                <FunnelStep label="Opened" count={email.opened} pct={email.opened > 0 ? Math.round((email.clicked / email.opened) * 100) : 0} />
                <FunnelStep label="Clicked" count={email.clicked} isLast />
              </Box>

              {/* Bounce rate warning */}
              {email.bounceRate > 5 && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ mt: 2 }}
                >
                  Bounce rate is {email.bounceRate}% ({email.bounced} bounced). Consider cleaning your guest list.
                </Alert>
              )}

              {/* Failed + bounced counts */}
              {(email.bounced > 0 || email.failed > 0) && (
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  {email.bounced > 0 && (
                    <Chip label={`${email.bounced} bounced`} color="warning" size="small" variant="outlined" />
                  )}
                  {email.failed > 0 && (
                    <Chip label={`${email.failed} failed`} color="error" size="small" variant="outlined" />
                  )}
                </Stack>
              )}
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* RSVP Timeline */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            RSVP Timeline
          </Typography>
          {isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="text" width="80%" sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : data?.rsvpTimeline && data.rsvpTimeline.length > 0 ? (
            <Box>
              {data.rsvpTimeline.map((entry) => (
                <Box
                  key={entry.date}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ minWidth: 70, fontSize: { xs: 12, sm: 13 } }}
                  >
                    {formatDate(entry.date)}
                  </Typography>
                  <Box sx={{ flex: 1, mx: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={rsvp && rsvp.totalGuests > 0
                        ? Math.min((entry.cumulative / rsvp.totalGuests) * 100, 100)
                        : 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, textAlign: 'right', fontSize: { xs: 11, sm: 12 } }}>
                    +{entry.daily} (total: {entry.cumulative}{rsvp ? `/${rsvp.totalGuests}` : ''})
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No RSVPs recorded yet.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Time to RSVP */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Time to RSVP
          </Typography>
          {isLoading ? (
            <Grid container spacing={1.5}>
              {[1, 2, 3, 4].map((i) => (
                <Grid size={{ xs: 6 }} key={i}>
                  <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : ttr && ttr.averageHours > 0 ? (
            <Box>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 12 } }}>
                      Average
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      {formatDuration(ttr.averageHours)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 12 } }}>
                      Median
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      {formatDuration(ttr.medianHours)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 12 } }}>
                      Fastest
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      {formatDuration(ttr.fastestHours)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 12 } }}>
                      Slowest
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      {formatDuration(ttr.slowestHours)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center', fontSize: { xs: 12, sm: 13 } }}
              >
                Most guests responded within {formatDuration(ttr.medianHours)} of receiving the invite
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No RSVP timing data available yet.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Email Timeline */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Email Activity Timeline
          </Typography>
          {isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="text" width="70%" sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : data?.emailTimeline && data.emailTimeline.length > 0 ? (
            <Box>
              {data.emailTimeline.map((entry) => (
                <Box
                  key={entry.date}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ minWidth: 70, fontSize: { xs: 12, sm: 13 } }}
                  >
                    {formatDate(entry.date)}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Chip
                      label={`${entry.opens} opens`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                    <Chip
                      label={`${entry.clicks} clicks`}
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                  </Stack>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No email activity recorded yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
